import React, { FC, Fragment, ReactNode } from 'react';
import { Button, Asset, AlertRoundIcon } from '@clearcover/atlas';
import styles from './styles.module.css';
import classNames from 'classnames';
import { getIn, useFormikContext } from 'formik';
import { CoveredPartyPayloadType } from '@clearcover/core-app-types';
import { RequiredDataTestIdProp } from 'Shared/utils/props/dataTestIdProp';

type ReportTableType = RequiredDataTestIdProp & {
  tableRows: Array<{
    data: Array<ReactNode>;
    formikChallengeLabel?: string;
    challenge?: 'edited' | 'removed';
  }>;
  headers: Array<string>;
  errorMessage?: string;
  isClaimsList?: boolean;
  autoColumnAlignment?: boolean;
};

export const DriverReportTable: FC<ReportTableType> = ({
  tableRows,
  headers,
  'data-testid': dataTestId,
  errorMessage,
  isClaimsList = false,
  autoColumnAlignment = false,
}) => {
  // This table is also rendered in read-only policy details today, which can
  // trigger Formik warnings there because that path is outside a Formik provider.
  const { setFieldValue, values } =
    useFormikContext<CoveredPartyPayloadType>() ?? {};
  const [firstColumn, source, ...rest] = headers;

  return (
    <div data-testid={dataTestId} className={styles.reportTable}>
      <div
        className={classNames(styles.reportTableHeaderRow, {
          [styles.claimsList]: isClaimsList,
        })}
        style={
          autoColumnAlignment
            ? { gridTemplateColumns: `repeat(${headers.length}, 3fr)` }
            : undefined
        }
      >
        {[firstColumn, source].map((header, index) => (
          <div
            className={classNames(styles.reportTableHeader, styles.small)}
            key={index}
          >
            {header}
          </div>
        ))}
        {rest.map((header, index) => (
          <div className={styles.reportTableHeader} key={index}>
            {header}
          </div>
        ))}
      </div>

      {errorMessage ? (
        <div className={styles.alertMessage}>
          <Asset
            icon={AlertRoundIcon}
            description={'Alert'}
            height={20}
            width={20}
            className={styles.alertIcon}
          />
          <span>{errorMessage}</span>
        </div>
      ) : (
        <>
          {tableRows.length > 0 ? (
            tableRows.map(
              ({ data, formikChallengeLabel, challenge }, index) => {
                const isRemoved =
                  getIn(values, formikChallengeLabel ?? '') === 'removed' ||
                  challenge === 'removed';
                const rowTestId = `${dataTestId}-row`;

                return (
                  <div
                    data-testid={rowTestId}
                    key={index}
                    className={classNames(styles.reportTableRow, {
                      [styles.removed]: isRemoved && !isClaimsList,
                      [styles.claimsList]: isClaimsList,
                    })}
                    style={
                      autoColumnAlignment
                        ? {
                            gridTemplateColumns: `repeat(${headers.length}, 3fr)`,
                          }
                        : undefined
                    }
                  >
                    {data.map((component, index) => (
                      <Fragment key={index}>{component}</Fragment>
                    ))}
                    {formikChallengeLabel && (
                      <div
                        className={classNames(
                          styles.reportData,
                          styles.cancelButton
                        )}
                      >
                        <Button
                          data-testid={`${rowTestId}-remove-button`}
                          size={'md'}
                          variant="danger-secondary"
                          className={styles.cancelButton}
                          type="button"
                          onClick={() =>
                            setFieldValue(
                              formikChallengeLabel,
                              !isRemoved ? 'removed' : 'edited'
                            )
                          }
                        >
                          {isRemoved ? 'Undo' : 'Remove'}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              }
            )
          ) : (
            <div className={styles.reportTableRow}>
              {Array(headers.length)
                .fill('-')
                .map((f, i) => (
                  <div key={`${f}-${i}`}>{f}</div>
                ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
